import React, { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';
import { useQuery } from '@apollo/client';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Grid from '@decisiv/ui-components/lib/components/Grid';
import Toggle from '@decisiv/ui-components/lib/components/Toggle';

import DashboardSection from 'components/DashboardSection';
import Loading from 'components/Loading';

import TotalAssetsCard from './TotalAssetsCard';
import TotalAssetGroupingsCard from './TotalAssetGroupingsCard';
import AssetsByStatusCard from './AssetsByStatusCard';
import ScheduledMaintenanceCard from './ScheduledMaintenanceCard';
import FaultsBySeverityCard from './FaultsBySeverityCard';
import ServiceEventsCard from './ServiceEventsCard';
import PendingApprovalsCard from './PendingApprovalsCard';
import AlertsByTypeCard from './AlertsByTypeCard';
import MostAlertsByDepotCard from './MostAlertsByDepotCard';

import businessOverviewPreferencesQuery from './businessOverviewPreferences.graphql';
import { AssignedToMeProvider, useAssignedToMe } from '../AssignedToMeContext';

const { Row } = Grid;

const TOGGLE_OPTIONS = [
  { id: 'all', text: t`All` },
  { id: 'assignedToMe', text: t`Assigned to Me` },
];

const useBusinessOverviewPreferences = () => {
  return useQuery(businessOverviewPreferencesQuery, {
    fetchPolicy: 'cache-and-network',
  });
};

function AssignedToMeToggle() {
  const {
    showOnlyAssignedToMe,
    toggleAssignedToMe,
    assignedAssetGroupingIds,
    loading,
  } = useAssignedToMe();

  const disabled = loading || assignedAssetGroupingIds.length === 0;

  const handleChange = useCallback(
    (selectedId) => {
      const shouldFilter = selectedId === 'assignedToMe';
      /* istanbul ignore next -- @preserve */
      if (shouldFilter !== showOnlyAssignedToMe) {
        toggleAssignedToMe();
      }
    },
    [showOnlyAssignedToMe, toggleAssignedToMe],
  );

  return (
    <Flex alignItems="center" gap={1}>
      <Toggle
        name="assignedToMeToggle"
        size="small"
        options={TOGGLE_OPTIONS}
        selected={showOnlyAssignedToMe ? 'assignedToMe' : 'all'}
        disabled={disabled}
        onChange={handleChange}
      />
    </Flex>
  );
}

function BusinessOverview() {
  const { loading, data } = useBusinessOverviewPreferences();

  const { dashboardPreferences } = data || {};
  const {
    showOnlyAssignedToMe: showOnlyAssignedToMePref,
    ...panelPreferences
  } = dashboardPreferences || {};

  const {
    totals,
    assetsByStatus,
    serviceEvents,
    alertsByType,
    mostAlertsByDepot,
    scheduledMaintenance,
    pendingApprovals,
    faultsBySeverity,
  } = panelPreferences;

  const lastRowActiveCards = useMemo(
    () =>
      [alertsByType, mostAlertsByDepot, scheduledMaintenance].filter(Boolean)
        .length,
    [alertsByType, mostAlertsByDepot, scheduledMaintenance],
  );

  if (loading) return <Loading />;

  return (
    <AssignedToMeProvider showOnlyAssignedToMe={showOnlyAssignedToMePref}>
      <DashboardSection
        title={t`Business Overview`}
        headerAction={<AssignedToMeToggle />}
      >
        <>
          <Grid.Container padding={0}>
            {totals && (
              <Row>
                <Grid.Column span={6} marginTop={2}>
                  <TotalAssetsCard />
                </Grid.Column>
                <Grid.Column span={6} marginTop={2}>
                  <TotalAssetGroupingsCard />
                </Grid.Column>
              </Row>
            )}
            {(assetsByStatus || serviceEvents) && (
              <Row>
                {assetsByStatus && (
                  <Grid.Column span={6} marginTop={2}>
                    <AssetsByStatusCard />
                  </Grid.Column>
                )}
                {serviceEvents && (
                  <Grid.Column span={6} marginTop={2}>
                    <ServiceEventsCard />
                  </Grid.Column>
                )}
              </Row>
            )}
            {(alertsByType || mostAlertsByDepot || scheduledMaintenance) && (
              <Row>
                {alertsByType && (
                  <Grid.Column span={4} marginTop={2}>
                    <AlertsByTypeCard
                      key={lastRowActiveCards}
                      showDividers={lastRowActiveCards === 1}
                    />
                  </Grid.Column>
                )}
                {mostAlertsByDepot && (
                  <Grid.Column span={4} marginTop={2}>
                    <MostAlertsByDepotCard />
                  </Grid.Column>
                )}
                {scheduledMaintenance && (
                  <Grid.Column span={4} marginTop={2}>
                    <ScheduledMaintenanceCard />
                  </Grid.Column>
                )}
              </Row>
            )}
            {(pendingApprovals || faultsBySeverity) && (
              <Row>
                {pendingApprovals && (
                  <Grid.Column span={4} marginTop={2}>
                    <PendingApprovalsCard />
                  </Grid.Column>
                )}
                {faultsBySeverity && (
                  <Grid.Column span={4} marginTop={2}>
                    <FaultsBySeverityCard />
                  </Grid.Column>
                )}
                <Grid.Column span={4} marginTop={2} />
              </Row>
            )}
          </Grid.Container>
        </>
      </DashboardSection>
    </AssignedToMeProvider>
  );
}

export default BusinessOverview;
